import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/platform';
import { useTheme } from '../ThemeContext';
import HomeHistoryReportBody from './HomeHistoryReportBody';
import {
  PRODUCT_NAME,
  INTERSTITIAL_TITLE,
  INTERSTITIAL_BODY,
  INTERSTITIAL_BUTTON,
  INTERSTITIAL_TERMS,
  REPORT_TERMS_PATH,
} from '../shared/homeHistoryReport';

const VIEWER_DESCRIPTION = "Owner's record of a home, shared by the owner. It is not a disclosure or an inspection.";
const CLOSED = 'This link is no longer available.';

function rememberMeta(name, content) {
  if (typeof document === 'undefined') return () => {};
  let node = document.querySelector(`meta[name="${name}"]`);
  const created = !node;
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute('name', name);
    document.head.appendChild(node);
  }
  const previous = node.getAttribute('content');
  node.setAttribute('content', content);
  return () => {
    if (created) node.remove();
    else if (previous == null) node.removeAttribute('content');
    else node.setAttribute('content', previous);
  };
}

function seenKey(token) {
  return `pacarion-report-open:${token}`;
}

async function publicPost(body) {
  const response = await fetch(getApiUrl('/.netlify/functions/home-report-public'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error((data && data.error) || CLOSED);
    error.status = response.status;
    throw error;
  }
  return data;
}

export default function HomeHistoryReportView({ token }) {
  const { colors } = useTheme();
  const [phase, setPhase] = useState('checking');
  const [snapshot, setSnapshot] = useState(null);
  const [files, setFiles] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [headshotUrl, setHeadshotUrl] = useState('');
  const [expiresLabel, setExpiresLabel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PRODUCT_NAME;
    const restoreDescription = rememberMeta('description', VIEWER_DESCRIPTION);
    const restoreRobots = rememberMeta('robots', 'noindex, nofollow');
    const restoreReferrer = rememberMeta('referrer', 'no-referrer');
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreRobots();
      restoreReferrer();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPhase('checking');
    setError('');
    publicPost({ token, action: 'meta' })
      .then(async (meta) => {
        if (cancelled) return;
        setExpiresLabel((meta && meta.expiresLabel) || '');
        let already = false;
        try {
          already = window.sessionStorage.getItem(seenKey(token)) === '1';
        } catch (err) {
          already = false;
        }
        if (!already) {
          setPhase('interstitial');
          return;
        }
        const data = await publicPost({ token, action: 'open' });
        if (cancelled) return;
        setSnapshot(data.snapshot);
        setFiles(data.files || {});
        setLogoUrl(data.logoUrl || '');
        setHeadshotUrl(data.headshotUrl || '');
        setPhase('report');
      })
      .catch((err) => {
        if (!cancelled) setError((err && err.message) || CLOSED);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function openReport() {
    setError('');
    try {
      window.sessionStorage.setItem(seenKey(token), '1');
    } catch (err) {
      // Private mode can block session storage. The interstitial can show again.
    }
    try {
      const data = await publicPost({ token, action: 'open' });
      setSnapshot(data.snapshot);
      setFiles(data.files || {});
      setLogoUrl(data.logoUrl || '');
      setHeadshotUrl(data.headshotUrl || '');
      setPhase('report');
    } catch (err) {
      setError((err && err.message) || CLOSED);
    }
  }

  const page = {
    minHeight: '100vh',
    background: colors.background,
    color: colors.text,
    padding: '1.25rem',
    boxSizing: 'border-box',
  };

  if (error) {
    return (
      <main style={page}>
        <h1 style={{ fontWeight: 400 }}>{PRODUCT_NAME}</h1>
        <p role="alert">{error}</p>
      </main>
    );
  }

  if (phase === 'checking') {
    return (
      <main style={page}>
        <h1 style={{ fontWeight: 400 }}>{PRODUCT_NAME}</h1>
      </main>
    );
  }

  if (phase !== 'report') {
    return (
      <main style={page}>
        <h1 style={{ fontWeight: 400 }}>{INTERSTITIAL_TITLE}</h1>
        <p style={{ lineHeight: 1.5, maxWidth: '40rem' }}>{INTERSTITIAL_BODY}</p>
        <p>
          {INTERSTITIAL_TERMS}{' '}
          <a href={REPORT_TERMS_PATH}>Report Viewer Terms</a>
        </p>
        <button
          type="button"
          onClick={openReport}
          style={{
            background: '#59c4c4',
            color: '#fff',
            border: 'none',
            borderRadius: '0.5rem',
            minHeight: '44px',
            padding: '0.75rem 1.1rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {INTERSTITIAL_BUTTON}
        </button>
      </main>
    );
  }

  return (
    <main style={page}>
      <HomeHistoryReportBody
        snapshot={snapshot}
        files={files}
        logoUrl={logoUrl}
        headshotUrl={headshotUrl}
        expiresLabel={expiresLabel}
        termsUrl={REPORT_TERMS_PATH}
      />
    </main>
  );
}
